import { useEffect, useState } from 'react';
import { BsArrowLeft, BsArrowRight } from 'react-icons/bs';
import './Pagination.scss';
import { PaginationProps } from '../../types';

const Pagination = ({
  totalPages,
  currentPage,
  hasNextPage,
  hasPrevPage,
  onPageChange,
  onNextPage,
  onPrevPage,
  limit,
  maxLimit = 250,
  onLimitChange,
}: PaginationProps) => {
  const [page, setPage] = useState<number>(currentPage);
  const [localLimit, setLocalLimit] = useState<number>(limit ?? 20);

  const isCursorMode = totalPages === undefined;

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (limit !== undefined && limit !== localLimit) {
      setLocalLimit(limit);
    }
  }, [limit]);

  const getPage = (index: number) => {
    if (!onPageChange || isCursorMode) return;
    const clamped = Math.min(Math.max(index, 1), totalPages!);
    setPage(clamped);
    onPageChange(clamped);
  };

  const handlePrev = () => {
    if (isCursorMode) {
      onPrevPage?.();
    } else {
      getPage(page - 1);
    }
  };

  const handleNext = () => {
    if (isCursorMode) {
      onNextPage?.();
    } else {
      getPage(page + 1);
    }
  };

  const applyLimit = (value: number) => {
    const clamped = Math.min(Math.max(value, 1), maxLimit);
    setLocalLimit(clamped);
    onLimitChange?.(clamped);
  };

  const isPrevDisabled = isCursorMode ? !hasPrevPage : currentPage <= 1;
  const isNextDisabled = isCursorMode ? !hasNextPage : currentPage >= (totalPages ?? 1);

  return (
    <div className="pagination">
      {limit !== undefined && onLimitChange && (
        <div className="pagination__limit">
          <span>Show</span>
          <input
            value={localLimit}
            onBlur={(e) => applyLimit(parseInt(e.target.value) || limit)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyLimit(parseInt((e.target as HTMLInputElement).value) || limit);
            }}
            onChange={(e) => setLocalLimit(isNaN(+e.target.value) ? limit : +e.target.value)}
            max={maxLimit}
            min={1}
          />
          <span>rows</span>
        </div>
      )}

      <div className="pagination__pager">
        <span>Page</span>
        {isCursorMode ? (
          <span className="pagination__page-number">{currentPage}</span>
        ) : (
          <>
            <input
              value={page}
              onBlur={(e) => getPage(parseInt(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') getPage(parseInt((e.target as HTMLInputElement).value));
              }}
              onChange={(e) => setPage(isNaN(+e.target.value) ? currentPage : +e.target.value)}
              max={totalPages}
            />
            <span>Of {totalPages}</span>
          </>
        )}

        <button type="button" aria-label="Previous page" onClick={handlePrev} disabled={isPrevDisabled}>
          <BsArrowLeft />
        </button>

        <button type="button" aria-label="Next page" onClick={handleNext} disabled={isNextDisabled}>
          <BsArrowRight />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
